'use client';

import type { ReactNode } from 'react';
import { cn } from '../../../lib';
import { useWishlistProductToggle } from '../hooks/use-wishlist-product-toggle';
import { useIsWishlistAvailable } from '../hooks/use-wishlist-safe';

interface WishlistProductButtonProps {
  productGid: string;
  variantGid: string;
  className?: string;
  /**
   * Strategy for adding items to wishlist
   * - 'all-items': Add to "All Items" without specific board (default)
   * - 'default-board': Add to the user's default board
   */
  addStrategy?: 'all-items' | 'default-board';
  /**
   * Custom children to override the default heart icon
   * If provided, you're responsible for the entire button content
   */
  children?: ReactNode;
}

/**
 * Shared wishlist product button component that consolidates all wishlist
 * toggle functionality across TileCloud, Yabby, and Trade apps.
 *
 * Features:
 * - Optimistic updates for immediate UI feedback
 * - Supports both "All Items" and "Default Board" add strategies
 * - Consistent heart icon with hover/active states
 * - Loading states and accessibility
 * - Error handling with automatic state reversion
 */
export function WishlistProductButton({
  productGid,
  variantGid,
  className,
  addStrategy = 'all-items',
  children,
}: WishlistProductButtonProps) {
  const isWishlistAvailable = useIsWishlistAvailable();
  const { isInWishlist, isLoading, isReady, handleToggle } =
    useWishlistProductToggle({
      productGid,
      variantGid,
      addStrategy,
    });

  // Show loading fallback during initial load
  if (!isWishlistAvailable) {
    return (
      <button
        type="button"
        className={cn(
          '!mt-0 firstwish-badge group/firstwish absolute top-3 right-3 z-10 p-1',
          className
        )}
        disabled
        aria-label="Wishlist (Loading)"
      >
        {children || <WishlistIcon isInWishlist={false} isLoading />}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        '!mt-0 firstwish-badge group/firstwish absolute top-3 right-3 z-10 p-1',
        className
      )}
      onClick={handleToggle}
      disabled={isLoading || !isReady}
      aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      {children || (
        <WishlistIcon isInWishlist={isInWishlist} isLoading={isLoading} />
      )}
    </button>
  );
}

interface WishlistIconProps {
  isInWishlist: boolean;
  isLoading?: boolean;
}

/**
 * Shared wishlist heart icon component with consistent styling
 * across all apps. Uses the same SVG path and styling patterns.
 */
export function WishlistIcon({
  isInWishlist,
  isLoading = false,
}: WishlistIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="!relative pointer-events-none h-6 w-6 select-none stroke-[1.5px]"
    >
      <title>Wishlist</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        className={cn(
          'fill-white stroke-white transition-colors duration-200',
          !isInWishlist && 'group-hover/firstwish:fill-current',
          isInWishlist && 'fill-current stroke-current',
          isLoading && 'opacity-60'
        )}
        d="M11.9932 5.13581C9.9938 2.7984 6.65975 2.16964 4.15469 4.31001C1.64964 6.45038 1.29697 10.029 3.2642 12.5604C4.89982 14.6651 9.84977 19.1041 11.4721 20.5408C11.6536 20.7016 11.7444 20.7819 11.8502 20.8135C11.9426 20.8411 12.0437 20.8411 12.1361 20.8135C12.2419 20.7819 12.3327 20.7016 12.5142 20.5408C14.1365 19.1041 19.0865 14.6651 20.7221 12.5604C22.6893 10.029 22.3797 6.42787 19.8316 4.31001C17.2835 2.19216 13.9925 2.7984 11.9932 5.13581Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
